import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ShootingSchedule } from './models/shooting-schedule.model';
import { Data } from '../../core/services/data';

interface SortConfig {
  column: keyof ShootingSchedule | '';
  direction: 'asc' | 'desc' | '';
}

@Component({
  selector: 'app-shooting-schedule',
  imports: [FormsModule],
  templateUrl: './shooting-schedule.html',
  styleUrl: './shooting-schedule.css'
})
export class ShootingScheduleComponent implements OnInit {
  private dataService = inject(Data);

  schedules = signal<ShootingSchedule[]>([]);
  searchTerm = signal('');
  sortConfig = signal<SortConfig>({ column: '', direction: '' });
  isLoading = signal(true);
  error = signal<string | null>(null);

  filteredSchedules = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    let filtered = this.schedules();

    if (term) {
      filtered = filtered.filter(schedule =>
        schedule.anlage.toLowerCase().includes(term) ||
        schedule.stand.toLowerCase().includes(term) ||
        this.formatDate(schedule.datum).toLowerCase().includes(term) ||
        schedule.personenVerein.some(pv =>
          pv.name.toLowerCase().includes(term) ||
          pv.anmerkung.toLowerCase().includes(term)
        ) ||
        (schedule.leitung && schedule.leitung.some(l => l.toLowerCase().includes(term)))
      );
    }

    const config = this.sortConfig();
    if (config.column) {
      return this.sortSchedules([...filtered], config);
    }

    return filtered;
  });

  ngOnInit(): void {
    this.loadSchedules();
  }

  loadSchedules(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.dataService.getShootingSchedule().subscribe({
      next: (data) => {
        this.schedules.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading shooting schedule:', err);
        this.error.set('Fehler beim Laden des Schießplans');
        this.isLoading.set(false);
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  onSort(column: keyof ShootingSchedule): void {
    const currentConfig = this.sortConfig();

    if (currentConfig.column === column) {
      if (currentConfig.direction === 'asc') {
        this.sortConfig.set({ column, direction: 'desc' });
      } else if (currentConfig.direction === 'desc') {
        this.sortConfig.set({ column: '', direction: '' });
      }
    } else {
      this.sortConfig.set({ column, direction: 'asc' });
    }
  }

  private sortSchedules(schedules: ShootingSchedule[], config: SortConfig): ShootingSchedule[] {
    if (!config.column) return schedules;

    return schedules.sort((a, b) => {
      const column = config.column as keyof ShootingSchedule;
      let aVal: any = a[column];
      let bVal: any = b[column];

      if (aVal instanceof Date && bVal instanceof Date) {
        return config.direction === 'asc'
          ? aVal.getTime() - bVal.getTime()
          : bVal.getTime() - aVal.getTime();
      }

      if (Array.isArray(aVal) && Array.isArray(bVal)) {
        aVal = aVal.join(', ');
        bVal = bVal.join(', ');
      }

      // Handle nested objects (like answesenheit)
      if (typeof aVal === 'object' && aVal !== null) {
        aVal = JSON.stringify(aVal);
      }
      if (typeof bVal === 'object' && bVal !== null) {
        bVal = JSON.stringify(bVal);
      }

      const comparison = String(aVal).localeCompare(String(bVal), 'de-CH');
      return config.direction === 'asc' ? comparison : -comparison;
    });
  }

  getSortIcon(column: keyof ShootingSchedule): string {
    const config = this.sortConfig();
    if (config.column !== column) return '↕';
    return config.direction === 'asc' ? '↑' : '↓';
  }

  isSortedColumn(column: keyof ShootingSchedule): boolean {
    return this.sortConfig().column === column;
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('de-CH', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }
}
